import {
  HHButton,
  HHError,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { DataGridPro, GridPaginationModel } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { useDynamicDialogContext } from '../../context/dynamic-dialog.context';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import { useFilterUrlSync } from '../../hooks/use-filter-url-sync';
import {
  EsiFeeScheduleType,
  PaymentType,
  useDeleteEsiFeeScheduleMutation,
  useUpdateEsiFeeScheduleMutation,
} from '../../types';
import { NoRowsOverlay } from '../grid-components/no-rows-or-error-overlay';
import {
  defaultEsiFeeScheduleFilters,
  EsiFeeScheduleFilterBar,
  EsiFeeScheduleFilterParams,
} from './esi-fee-schedule-filter-bar';
import { EsiFeeScheduleForm } from './esi-fee-schedule-form/form';
import { EsiFeeScheduleFormValues } from './esi-fee-schedule-form/validation-schema';
import { useEsiFeeScheduleColumns } from './use-esi-fee-schedule-columns';
import { useEsiFeeScheduleQueries } from './use-esi-fee-schedule-queries';

export const ESI_FEE_SCHEDULE_GRID_TEST_ID = 'esi-fee-schedule-grid';

const DEFAULT_PAGE_SIZE = 50;

const applyFilters = (
  rows: EsiFeeScheduleType[],
  filters: EsiFeeScheduleFilterParams,
): EsiFeeScheduleType[] =>
  rows.filter(row => {
    if (
      filters.programs.length > 0 &&
      (!row.program || !filters.programs.includes(row.program))
    ) {
      return false;
    }

    if (
      filters.paymentTypes.length > 0 &&
      !filters.paymentTypes.includes(row.paymentType)
    ) {
      return false;
    }

    if (filters.priceMin !== '') {
      const min = Number(filters.priceMin);

      if (!isNaN(min) && Number(row.price) < min) {
        return false;
      }
    }

    if (filters.priceMax !== '') {
      const max = Number(filters.priceMax);

      if (!isNaN(max) && Number(row.price) > max) {
        return false;
      }
    }

    if (
      filters.upc !== '' &&
      !(row.upc ?? '').toLowerCase().includes(filters.upc.toLowerCase())
    ) {
      return false;
    }

    return true;
  });

interface EsiFeeScheduleGridProps {
  paymentTypes: PaymentType[];
  paymentTypesLoading: boolean;
}

export const EsiFeeScheduleGrid = ({
  paymentTypes,
  paymentTypesLoading,
}: EsiFeeScheduleGridProps): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { rows, loading, hasError } = useEsiFeeScheduleQueries();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setDialogContent, closeDialog } = useDynamicDialogContext();
  const { setSnackbarState } = useSnackbarContext();

  const [filterParams, setFilterParams] = useFilterUrlSync(
    defaultEsiFeeScheduleFilters,
  );

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const handleFiltersApplied = useCallback((): void => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  const filteredRows = useMemo(
    () => applyFilters(rows, filterParams),
    [rows, filterParams],
  );

  const [updateEsiFeeSchedule] = useUpdateEsiFeeScheduleMutation();

  const [deleteEsiFeeSchedule] = useDeleteEsiFeeScheduleMutation();

  const handleEdit = useCallback(
    (row: EsiFeeScheduleType): void => {
      const initialValues: EsiFeeScheduleFormValues = {
        program: row.program ?? '',
        paymentType: row.paymentType,
        price: row.price,
        upc: row.upc,
        description: row.description ?? '',
      };

      const handleUpdate = async (
        values: EsiFeeScheduleFormValues,
      ): Promise<void> => {
        try {
          await updateEsiFeeSchedule({
            variables: {
              id: row.id,
              esiFeeScheduleInput: {
                paymentType: values.paymentType,
                price: values.price as number,
                upc: values.upc,
                program: values.program || null,
                description: values.description || null,
              },
            },
            refetchQueries: ['getEsiFeeSchedules'],
          });
          setSnackbarState(
            'ESI fee schedule updated successfully',
            'success',
            3000,
          );
          onClose();
        } catch {
          setSnackbarState('Error updating ESI fee schedule', 'error', 3000);
        }
      };

      setDrawerContent(
        <HHStack padding={4}>
          <EsiFeeScheduleForm
            onSubmit={handleUpdate}
            initialValues={initialValues}
            paymentTypes={paymentTypes}
            paymentTypesLoading={paymentTypesLoading}
          />
        </HHStack>,
        <HHTypography hhVariant="h6">Edit ESI Fee Schedule</HHTypography>,
        undefined,
        'temporary',
      );
    },
    [
      setDrawerContent,
      updateEsiFeeSchedule,
      paymentTypes,
      paymentTypesLoading,
      setSnackbarState,
      onClose,
    ],
  );

  const handleDelete = useCallback(
    (row: EsiFeeScheduleType): void => {
      const executeDelete = async (): Promise<void> => {
        closeDialog();
        try {
          await deleteEsiFeeSchedule({
            variables: { id: row.id },
            refetchQueries: ['getEsiFeeSchedules'],
          });
          setSnackbarState(
            'ESI fee schedule deleted successfully',
            'success',
            3000,
          );
        } catch {
          setSnackbarState('Error deleting ESI fee schedule', 'error', 3000);
        }
      };

      setDialogContent({
        title: (
          <HHTypography hhVariant="section-title">
            Delete ESI Fee Schedule
          </HHTypography>
        ),
        content: (
          <HHStack spacing={2}>
            <HHTypography hhVariant="body1">
              Are you sure you want to delete this ESI fee schedule (
              {row.paymentType} — {row.upc})?
            </HHTypography>
          </HHStack>
        ),
        footer: (
          <HHStack direction="row" spacing={2} padding={1}>
            <HHButton
              hhVariant="variant-bypass"
              variant="outlined"
              onClick={closeDialog}
              data-testid="esi-fee-schedule-delete-cancel-button"
            >
              Cancel
            </HHButton>
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              color="error"
              onClick={(): void => {
                void executeDelete();
              }}
              data-testid="esi-fee-schedule-delete-confirm-button"
            >
              Delete
            </HHButton>
          </HHStack>
        ),
        width: 'sm',
        fullWidth: true,
      });
    },
    [setDialogContent, closeDialog, deleteEsiFeeSchedule, setSnackbarState],
  );

  const columns = useEsiFeeScheduleColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    canWrite,
  });

  if (hasError) {
    return (
      <HHError
        body="Error fetching ESI fee schedules"
        hhVariant="variant-bypass"
      />
    );
  }

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={6}
      justifyContent="space-between"
      minHeight="0"
    >
      <HHStack paddingLeft={6}>
        <EsiFeeScheduleFilterBar
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          rows={rows}
          onFiltersApplied={handleFiltersApplied}
        />
      </HHStack>

      <DataGridPro
        columns={columns}
        rows={filteredRows}
        loading={loading}
        getRowId={(row): number => row.id}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="client"
        initialState={{
          sorting: { sortModel: [{ field: 'program', sort: 'asc' }] },
        }}
        sortingMode="client"
        filterMode="client"
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        pinnedColumns={{ right: ['actions'] }}
        aria-label="ESI fee schedules table"
        data-testid={ESI_FEE_SCHEDULE_GRID_TEST_ID}
        {...(process.env.NODE_ENV === 'test' && {
          disableVirtualization: true,
        })}
        slots={{
          noResultsOverlay: NoRowsOverlay,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          noResultsOverlay: { error: hasError } as Record<string, boolean>,
          noRowsOverlay: { error: hasError } as Record<string, boolean>,
        }}
      />
    </HHStack>
  );
};
